// Batch E2.1 — Horizontal stage tab strip used above the Portfolio list.
//
// Renders "All (N) · Prospect (n) · Watching (n) · …" using
// usePipelineCounts() so the counts stay in sync with PATCH /stage
// mutations (the hook is invalidated by StageBadge / StagePicker on
// every successful change).
//
// Cold-load skeleton (post-tester-feedback fix):
//   useStages() returns an empty array on the first paint before the
//   GET /api/pipeline/stages network round-trip resolves. Without a
//   skeleton, the strip rendered with only the "All" tab visible — the
//   per-stage tabs (including Negotiating) flickered in ~150ms later
//   and a tester caught the flicker, reading it as "Negotiating tab
//   missing". The skeleton below reserves the layout so the strip
//   never reflows.
//
// Active state styling matches the existing Portfolio verdict-pill
// pattern (filled indigo pill when selected), so the new tab strip
// reads as the same primary control as the legacy verdict filter
// that lives further down the same card.

import { useStages } from "./StageBadge";
import usePipelineCounts from "@/lib/usePipelineCounts";

export default function StageTabs({ active = "all", onChange }) {
  const stages = useStages();
  const { counts, total } = usePipelineCounts();

  // Cold-load skeleton — 9 pulsing pills (1 "All" + 8 stages) sized
  // close enough to the real pills that the layout doesn't shift when
  // the metadata arrives.
  if (stages.length === 0) {
    return (
      <div
        className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1"
        data-testid="stage-tabs"
        data-loading="true"
        role="tablist"
        aria-busy="true"
        aria-label="Filter portfolio by pipeline stage"
      >
        {Array.from({ length: 9 }).map((_, i) => (
          <span
            key={i}
            data-testid={`stage-tab-skeleton-${i}`}
            className="inline-flex h-7 w-24 flex-shrink-0 animate-pulse rounded-full bg-slate-100"
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  // "All" + the 8 canonical stages, in the canonical order.
  const items = [
    { id: "all", label: "All", count: total },
    ...stages.map((s) => ({
      id: s.id,
      label: s.label,
      count: counts ? counts[s.id] ?? 0 : null,
      colors: s.colors,
    })),
  ];

  return (
    <div
      className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1"
      data-testid="stage-tabs"
      role="tablist"
      aria-label="Filter portfolio by pipeline stage"
    >
      {items.map((item) => {
        const isActive = item.id === active;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-testid={`stage-tab-${item.id}`}
            data-stage-active={isActive ? "true" : "false"}
            onClick={() => onChange && onChange(item.id)}
            className={`group inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-all active:scale-[0.97] ${
              isActive
                ? "border-indigo-600 bg-indigo-600 text-white shadow-sm"
                : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
            }`}
          >
            <span>{item.label}</span>
            <span
              className={`inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
                isActive
                  ? "bg-white/20 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {item.count == null ? "—" : item.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
